import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { API_BASE } from "../api/config";
import "./ErpInventoryCatalogOverview.css";

const SHOP_LIST = [
  "ALL",
  "DNGHT",
  "DNGPF",
  "GMP",
  "JCHT",
  "JCPF",
  "JS",
  "MDYHT",
  "MDYPF",
  "MT",
  "OSHT",
  "OSPF",
  "STHT",
  "STPF",
];

function money(value) {
  return Number(value || 0).toLocaleString();
}

export default function ErpInventoryCatalogOverview() {
  const [shop, setShop] = useState("ALL");
  const [active, setActive] = useState("active");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState("");
  const [error, setError] = useState("");

  const query = useMemo(() => {
    const q = new URLSearchParams();
    q.set("shop", shop);
    q.set("active", active);
    q.set("search", search);
    q.set("limit", "1000");
    return q.toString();
  }, [shop, active, search]);

  async function loadRows() {
    try {
      setLoading(true);
      setError("");

      const res = await fetch(`${API_BASE}/api/erp/inventory-catalog-overview?${query}`);
      const json = await res.json().catch(() => ({}));

      if (!res.ok || json.success === false) {
        throw new Error(json.error || json.message || "Inventory overview loading failed");
      }

      setRows(Array.isArray(json.rows) ? json.rows : []);
      setTotal(Number(json.total || 0));
    } catch (err) {
      setError(err.message || String(err));
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  async function patchRow(row, payload) {
    try {
      setSavingId(String(row.id));
      setError("");

      const res = await fetch(`${API_BASE}/api/erp/inventory-catalog/${row.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok || json.success === false) {
        throw new Error(json.error || json.message || "Update failed");
      }

      await loadRows();
      alert("Updated successfully");
    } catch (err) {
      alert(err.message || String(err));
    } finally {
      setSavingId("");
    }
  }

  async function editPrice(row) {
    // BULK_PRICE_BY_PRODUCT_CODE_V1
    // Product Code တူတဲ့ item အားလုံးကို shop/color/size မခွဲဘဲ price တစ်ပြိုင်နက်ပြောင်းမယ်။
    const productCode = String(row.product_code || "").trim().toUpperCase();

    const input = window.prompt(
      `New price for PRODUCT CODE: ${productCode}\n\nThis will update ALL shops, ALL colors, ALL sizes for this product code.`,
      String(Number(row.selling_price_mmk || 0))
    );

    if (input === null) return;

    const price = Number(input);

    if (!Number.isFinite(price) || price < 0) {
      alert("Invalid price");
      return;
    }

    const ok = window.confirm(
      `Confirm bulk price update?\n\nProduct Code: ${productCode}\nNew Price: ${price.toLocaleString()} Ks\n\nApply to: ALL shops + ALL colors + ALL sizes`
    );

    if (!ok) return;

    try {
      setSavingId(String(row.id));
      setError("");

      const res = await fetch(
        `${API_BASE}/api/erp/inventory-catalog/product/${encodeURIComponent(productCode)}/price`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            selling_price_mmk: price,
            includeInactive: false,
          }),
        }
      );

      const json = await res.json().catch(() => ({}));

      if (!res.ok || json.success === false) {
        throw new Error(json.error || json.message || "Bulk price update failed");
      }

      await loadRows();

      alert(
        `Price updated successfully.\n\nProduct Code: ${productCode}\nUpdated rows: ${json.updatedCount || 0}`
      );
    } catch (err) {
      alert(err.message || String(err));
    } finally {
      setSavingId("");
    }
  }

  async function editSize(row) {
    const input = window.prompt(
      `New size for ${row.location_code} / ${row.product_code} / ${row.color}`,
      String(row.size || "-")
    );

    if (input === null) return;

    const size = String(input || "-").trim() || "-";
    await patchRow(row, { size });
  }

  async function editColor(row) {
    const input = window.prompt(
      `New color for ${row.location_code} / ${row.product_code} / ${row.size}`,
      String(row.color || "-")
    );

    if (input === null) return;

    const color = String(input || "-").trim() || "-";
    await patchRow(row, { color });
  }

  async function deleteRow(row) {
    const ok = window.confirm(
      `Delete / hide this product?\n\n${row.location_code} / ${row.product_code} / ${row.color} / ${row.size}\n\nThis will soft-delete only. Sale history will not be deleted.`
    );

    if (!ok) return;

    try {
      setSavingId(String(row.id));
      setError("");

      const res = await fetch(`${API_BASE}/api/erp/inventory-catalog/${row.id}`, {
        method: "DELETE",
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok || json.success === false) {
        throw new Error(json.error || json.message || "Delete failed");
      }

      await loadRows();
      alert("Product hidden / deleted from active list");
    } catch (err) {
      alert(err.message || String(err));
    } finally {
      setSavingId("");
    }
  }

  async function reactivateRow(row) {
    await patchRow(row, { is_active: true });
  }

  return (
    <Layout>
      <div className="catalog-page">
        <div className="catalog-header">
          <div>
            <h1>ERP Inventory All Shop Overview</h1>
            <p>All shop product catalog, stock qty, price, size, color edit and soft delete.</p>
          </div>

          <button type="button" onClick={loadRows} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>

        <div className="catalog-filters">
          <label>
            Shop
            <select value={shop} onChange={(e) => setShop(e.target.value)}>
              {SHOP_LIST.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>

          <label>
            Active
            <select value={active} onChange={(e) => setActive(e.target.value)}>
              <option value="active">Active only</option>
              <option value="inactive">Deleted / inactive</option>
              <option value="all">All</option>
            </select>
          </label>

          <label className="catalog-search">
            Search
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Product code / color / size / brand / shop"
            />
          </label>
        </div>

        {error ? <div className="catalog-error">{error}</div> : null}

        <div className="catalog-summary">
          Showing <b>{rows.length}</b> rows / Total <b>{total}</b>
        </div>

        <div className="catalog-table-wrap">
          <table className="catalog-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Shop</th>
                <th>Product Code</th>
                <th>Color</th>
                <th>Size</th>
                <th>Brand</th>
                <th>Qty</th>
                <th>Price</th>
                <th>Active</th>
                <th>Updated</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="11" className="empty-cell">
                    Loading...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="11" className="empty-cell">
                    No products found
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className={row.is_active ? "" : "inactive-row"}>
                    <td>{row.id}</td>
                    <td><b>{row.location_code}</b></td>
                    <td className="code-cell">{row.product_code}</td>
                    <td>{row.color}</td>
                    <td>{row.size}</td>
                    <td>{row.brand || "-"}</td>
                    <td className={Number(row.qty || 0) <= 0 ? "zero-qty" : ""}>
                      {money(row.qty)}
                    </td>
                    <td>{money(row.selling_price_mmk)} Ks</td>
                    <td>{row.is_active ? "Active" : "Inactive"}</td>
                    <td>{row.updated_at ? new Date(row.updated_at).toLocaleString() : "-"}</td>
                    <td className="action-cell">
                      <button disabled={savingId === String(row.id)} onClick={() => editPrice(row)}>
                        Price
                      </button>
                      <button disabled={savingId === String(row.id)} onClick={() => editSize(row)}>
                        Size
                      </button>
                      <button disabled={savingId === String(row.id)} onClick={() => editColor(row)}>
                        Color
                      </button>
                      {row.is_active ? (
                        <button className="danger-btn" disabled={savingId === String(row.id)} onClick={() => deleteRow(row)}>
                          Delete
                        </button>
                      ) : (
                        <button disabled={savingId === String(row.id)} onClick={() => reactivateRow(row)}>
                          Restore
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
